import { supabase } from '@/integrations/supabase/client';

export class AudioRecorder {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  constructor(private onAudioData: (audioData: Float32Array) => void) {}

  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      return this.startWithStream(this.stream);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }

  async startWithStream(mediaStream: MediaStream) {
    try {
      this.stream = mediaStream;
      
      this.audioContext = new AudioContext({
        sampleRate: 24000,
      });

      // Resume audio context if suspended (mobile requirement)
      if (this.audioContext.state === 'suspended') {
        console.log('Resuming audio context for recording');
        await this.audioContext.resume();
      }
      
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      
      this.processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        this.onAudioData(new Float32Array(inputData));
      };
      
      this.source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
    } catch (error) {
      console.error('Error setting up audio recorder with stream:', error);
      throw error;
    }
  }

  stop() {
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export interface VoiceAssistantToolConfig {
  name: string;
  description: string;
  parameters: Record<string, any>;
}

export class RealtimeChat {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioEl: HTMLAudioElement;
  private recorder: AudioRecorder | null = null;
  private isConnected = false;
  private mediaStream: MediaStream | null = null;
  private isResponseInProgress = false;
  private pendingResponseCreate = false;
  private tools: VoiceAssistantToolConfig[] = [];

  constructor(
    private onMessage: (message: any) => void,
    private brandId?: string,
    private userId?: string
  ) {
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
  }

  setTools(tools: VoiceAssistantToolConfig[]) {
    this.tools = tools;
    console.log('Voice assistant tools configured:', tools.length);
  }

  async init(brandId?: string, userId?: string) {
    try {
      console.log('Initializing realtime chat...');
      
      // Get ephemeral token from our Edge Function with brand context
      const { data, error } = await supabase.functions.invoke('realtime-tokens', {
        body: { brandId, userId }
      });
      
      if (error) {
        console.error('Error getting token:', error);
        throw new Error(`Failed to get ephemeral token: ${error.message}`);
      }
      
      if (!data.client_secret?.value) {
        console.error('No client secret in response:', data);
        throw new Error("Failed to get ephemeral token");
      }

      const EPHEMERAL_KEY = data.client_secret.value;
      console.log('Got ephemeral token');

      // Create peer connection
      this.pc = new RTCPeerConnection();

      // Set up remote audio
      this.pc.ontrack = e => {
        console.log('Received remote audio track');
        this.audioEl.srcObject = e.streams[0];
      };

      // Add local audio track - get media stream once for both WebRTC and recording
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      this.pc.addTrack(this.mediaStream.getTracks()[0]);
      console.log('Added local audio track');

      // Set up data channel
      this.dc = this.pc.createDataChannel("oai-events");
      this.dc.addEventListener("message", async (e) => {
        try {
          const event = JSON.parse(e.data);
          console.log("Received event:", event.type, event);
          
          // Track response state
          if (event.type === 'response.created') {
            this.isResponseInProgress = true;
            console.log('Response started');
          } else if (event.type === 'response.done') {
            this.isResponseInProgress = false;
            console.log('Response completed');
            // If we had a pending response request, send it now
            if (this.pendingResponseCreate) {
              this.pendingResponseCreate = false;
              this.safeResponseCreate();
            }
          }
          
          // Handle function calls
          if (event.type === 'response.function_call_arguments.done') {
            await this.handleFunctionCall(event);
          } else {
            this.onMessage(event);
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      });

      this.dc.addEventListener("open", () => {
        console.log('Data channel opened, sending session configuration...');
        this.isConnected = true;
        this.sendSessionConfig();
      });

      this.dc.addEventListener("close", () => {
        console.log('Data channel closed');
        this.isConnected = false;
      });

      // Create and set local description
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      console.log('Created offer');

      // Connect to OpenAI's Realtime API
      const baseUrl = "https://api.openai.com/v1/realtime";
      const model = "gpt-4o-realtime-preview-2024-12-17";
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          "Content-Type": "application/sdp"
        },
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error('SDP response error:', sdpResponse.status, errorText);
        throw new Error(`Failed to connect to OpenAI: ${sdpResponse.status}`);
      }

      const answer = {
        type: "answer" as RTCSdpType,
        sdp: await sdpResponse.text(),
      };
      
      await this.pc.setRemoteDescription(answer);
      console.log("WebRTC connection established");

    } catch (error) {
      console.error("Error initializing chat:", error);
      throw error;
    }
  }

  private sendSessionConfig() {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Cannot send session config: data channel not open');
      return;
    }

    // Build dynamic tools from configured tools
    const openAITools = this.tools.map(tool => ({
      type: "function",
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters
    }));

    // Generate tool descriptions for instructions
    const toolDescriptions = this.tools.map(t => `- ${t.name}: ${t.description}`).join('\n');

    const instructions = `You are a helpful AI business assistant that can access real business data and help with tasks. You have access to the following functions to help users:

${toolDescriptions}

Always be helpful and provide clear, actionable insights based on the data you retrieve.

Current brand context: You are assisting with business operations, task management, and SEO content creation.`;

    // Send session configuration with dynamic tools
    this.dc.send(JSON.stringify({
      type: "session.update", 
      session: {
        modalities: ["text", "audio"],
        instructions,
        voice: "alloy",
        input_audio_format: "pcm16",
        output_audio_format: "pcm16",
        input_audio_transcription: {
          model: "whisper-1"
        },
        turn_detection: {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 1000
        },
        tools: openAITools,
        tool_choice: "auto",
        temperature: 0.8,
        max_response_output_tokens: "inf"
      }
    }));
    
    console.log('Session configuration sent with', openAITools.length, 'tools');
  }

  private encodeAudioData(float32Array: Float32Array): string {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    
    const uint8Array = new Uint8Array(int16Array.buffer);
    let binary = '';
    const chunkSize = 0x8000;
    
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    
    return btoa(binary);
  }

  private safeResponseCreate() {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.log('Cannot create response: data channel not ready');
      return;
    }
    
    if (this.isResponseInProgress) {
      console.log('Response already in progress, queuing request');
      this.pendingResponseCreate = true;
      return;
    }
    
    console.log('Sending response.create');
    this.dc.send(JSON.stringify({ type: 'response.create' }));
  }

  async sendMessage(text: string) {
    if (!this.dc || this.dc.readyState !== 'open') {
      throw new Error('Data channel not ready');
    }

    console.log('Sending text message:', text);

    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text
          }
        ]
      }
    };

    this.dc.send(JSON.stringify(event));
    this.safeResponseCreate();
  }

  startRecording() {
    if (this.recorder || !this.mediaStream) {
      return;
    }

    console.log('Starting audio recording...');
    this.recorder = new AudioRecorder((audioData) => {
      if (this.dc?.readyState === 'open') {
        this.dc.send(JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: this.encodeAudioData(audioData)
        }));
      }
    });
    
    // Use the existing media stream instead of requesting a new one
    return this.recorder.startWithStream(this.mediaStream);
  }

  stopRecording() {
    if (this.recorder) {
      console.log('Stopping audio recording...');
      this.recorder.stop();
      this.recorder = null;
      // Signal end of audio buffer to the model and request a response
      if (this.dc?.readyState === 'open') {
        try {
          this.dc.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
          this.safeResponseCreate();
          console.log('Committed audio buffer and requested response');
        } catch (e) {
          console.error('Failed to commit audio buffer:', e);
        }
      }
    }
  }

  private async handleFunctionCall(event: any) {
    try {
      const functionName = event.name;
      const functionArgs = JSON.parse(event.arguments || '{}');
      const callId = event.call_id;
      
      console.log('Handling function call:', functionName, functionArgs, 'callId:', callId);
      
      // Call our function handler edge function using Supabase client (includes auth)
      const { data: result, error } = await supabase.functions.invoke('realtime-function-handler', {
        body: {
          functionName,
          functionArgs,
          brandId: this.brandId,
          userId: this.userId
        }
      });
      
      if (error) {
        throw new Error(`Function call failed: ${error.message}`);
      }
      console.log('Function call result:', result);
      
      if (this.dc?.readyState === 'open') {
        // Send function result back to OpenAI
        const functionOutput = result.success ? result.result : { error: result.error };
        
        this.dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(functionOutput)
          }
        }));
        
        console.log('Sent function result back to OpenAI');
        
        // Continue the conversation - wait for response.done before sending
        this.safeResponseCreate();
        
        console.log('Requested new response from OpenAI');
      } else {
        console.error('Data channel not open when trying to send function result');
      }
      
    } catch (error) {
      console.error('Function call error:', error);
      
      // Send error result
      if (this.dc?.readyState === 'open') {
        this.dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: event.call_id,
            output: JSON.stringify({ error: error.message })
          }
        }));
        
        // Still try to continue the conversation
        this.safeResponseCreate();
      }
    }
  }

  disconnect() {
    console.log('Disconnecting realtime chat...');
    this.stopRecording();
    this.dc?.close();
    this.pc?.close();
    
    // Clean up media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    
    this.isConnected = false;
  }

  updateInstructions(instructions: string) {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.error('Cannot update instructions: data channel not open');
      return;
    }

    console.log('Updating session instructions:', instructions);
    
    this.dc.send(JSON.stringify({
      type: "session.update", 
      session: {
        instructions: instructions
      }
    }));
  }

  get connected() {
    return this.isConnected;
  }
}